import React, { useState } from "react";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Mail, Copy, ExternalLink, CheckCircle, AlertTriangle, Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "@/hooks/use-toast";
import { discoveryRepository } from "../../repositories/discovery.repository";

export interface InfluencerContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  influencer: {
    id: string;
    name: string;
    handle: string;
    profilePic: string | null;
    hasEmail: boolean;
  };
}

export function InfluencerContactModal({ open, onOpenChange, influencer }: InfluencerContactModalProps) {
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const getInitials = (name: string) => {
    return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  };

  const handleRevealEmail = async () => {
    setIsLoading(true);
    setHasError(false);
    
    try {
      const data = await discoveryRepository.getInfluencerEmail(influencer.id);
      setEmail(data);
    } catch (error: any) {
      console.error("Failed to fetch email:", error);
      setHasError(true);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch email address",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyEmail = () => {
    if (email) {
      navigator.clipboard.writeText(email);
      toast({
        title: "Email copied",
        description: "Email address copied to clipboard",
      });
    }
  };

  const handleOpenMailClient = () => {
    if (email) {
      window.open(`mailto:${email}`, "_blank");
    }
  };

  // Reset state when modal closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setEmail(null);
      setHasError(false);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" />
            Contact Influencer
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Influencer Info */}
          <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
            <Avatar className="w-14 h-14 border-2 border-background shadow">
              <AvatarImage src={influencer.profilePic || undefined} alt={influencer.name} className="object-cover" />
              <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                {getInitials(influencer.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-foreground">{influencer.name}</h3>
              <p className="text-sm text-muted-foreground">{influencer.handle}</p>
            </div>
          </div>

          {influencer.hasEmail ? (
            <>
              {/* Email Source Badge */}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium border border-green-100">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Business Email
                </span>
              </div>

              {/* Email Display */}
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Email Address</span>
                  {!email && !hasError && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRevealEmail}
                      disabled={isLoading}
                      className="h-7 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      type="button"
                    >
                      {isLoading ? (
                        <LoadingSpinner size="small" label="Loading..." />
                      ) : (
                        "Reveal Email"
                      )}
                    </Button>
                  )}
                </div>
                <p className="text-lg font-medium text-foreground font-mono">
                  {email ? email : hasError ? "Failed to load" : "p***@***.***"}
                </p>
              </div>

              {/* Action Buttons - Only show when email is revealed */}
              {email && (
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={handleCopyEmail}
                    type="button"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Email
                  </Button>
                  <Button
                    className="flex-1 gap-2 bg-purple-600 hover:bg-purple-700"
                    onClick={handleOpenMailClient}
                    type="button"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Email Client
                  </Button>
                </div>
              )}

              {/* Responsible Use Notice */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  Please use this contact information responsibly. Avoid spam and respect the influencer's time.
                </p>
              </div>
            </>
          ) : (
            /* Empty State - No Email */
            <div className="text-center py-8 space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Email Not Available</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  This influencer has not shared an email publicly.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
